import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Slider,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  FlightTakeoff,
  Search,
  RequestQuote,
  WhatsApp,
  Tune,
  AddCircleOutline,
  RemoveCircleOutline,
  Luggage,
  AccessTime,
  Star,
  WarningAmber,
  Speed,
  AttachMoney,
  Explore,
} from '@mui/icons-material';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeaderV2, FooterV2 } from '../../components/layout';
import { colors } from '../../components/theme/colors';
import { FLIGHT_AIRPORTS, getAirportLabel } from '../../data/flightAirports';
import {
  coerceFlightSearchAirports,
  getCountriesForProductLine,
  getDefaultDestinationCountry,
  getDestinationAirports,
  getFlightScopeOptions,
  getOriginAirports,
} from '../../utils/locationHierarchy';
import FlightItineraryPanel from '../../components/flights/FlightItineraryPanel';

import { API_BASE_URL as API_BASE } from '../../config/api';
const WHATSAPP_PHONE = '923001234567'; // A.R Travels official customer WhatsApp line

const INITIAL_FLIGHT_SCOPE = 'International';
const INITIAL_DESTINATION_COUNTRY = getDefaultDestinationCountry(INITIAL_FLIGHT_SCOPE);
const INITIAL_AIRPORTS = coerceFlightSearchAirports(
  INITIAL_FLIGHT_SCOPE,
  'ISB',
  'JED',
  INITIAL_DESTINATION_COUNTRY
);

const FlightsPageV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const autoSearched = useRef(false);
  const returnToCustomize = Boolean(location.state?.returnToCustomize);

  // Trip scope: filters airport lists (International / Domestic / Umrah)
  const [flightScope, setFlightScope] = useState(location.state?.flightScope || INITIAL_FLIGHT_SCOPE);
  const [destinationCountry, setDestinationCountry] = useState(
    location.state?.destinationCountry ||
      getDefaultDestinationCountry(location.state?.flightScope || INITIAL_FLIGHT_SCOPE)
  );

  const originAirports = useMemo(() => getOriginAirports(), []);
  const destinationAirports = useMemo(
    () => getDestinationAirports(flightScope, destinationCountry),
    [flightScope, destinationCountry]
  );

  // Trip Type: 'one_way' | 'round_trip' | 'multi_city'
  const [tripType, setTripType] = useState('one_way');

  const [form, setForm] = useState({
    origin: INITIAL_AIRPORTS.origin,
    destination: INITIAL_AIRPORTS.destination,
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    cabinClass: 'economy',
  });

  // Multi-city legs
  const [legs, setLegs] = useState([
    { origin: 'ISB', destination: 'DXB', departureDate: '' },
    { origin: 'DXB', destination: 'JED', departureDate: '' },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);

  // Filtering & Sorting (PRD Section 6.4 & 8)
  const [activeCategoryTab, setActiveCategoryTab] = useState('all'); // 'all' | 'BestOverall' | 'Cheapest' | 'Fastest'
  const [showFilters, setShowFilters] = useState(false);
  const [filterStops, setFilterStops] = useState('all'); // 'all' | '0' | '1'
  const [filterAirline, setFilterAirline] = useState('all');
  const [maxPriceFilter, setMaxPriceFilter] = useState(2500);
  const [sortBy, setSortBy] = useState('recommended'); // 'recommended' | 'price_asc' | 'duration_asc' | 'dep_asc'

  // Quote Request Form
  const [requestForm, setRequestForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    notes: '',
  });
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestDone, setRequestDone] = useState(null);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleTripTypeChange = (event, newValue) => {
    setTripType(newValue);
  };

  const handleFlightScopeChange = (event, newScope) => {
    if (!newScope) return;
    setFlightScope(newScope);
    const country = getDefaultDestinationCountry(newScope);
    setDestinationCountry(country);
    const coerced = coerceFlightSearchAirports(newScope, form.origin, form.destination, country);
    setForm((prev) => ({ ...prev, origin: coerced.origin, destination: coerced.destination }));
  };

  const handleDestinationCountryChange = (e) => {
    const country = e.target.value;
    setDestinationCountry(country);
    const coerced = coerceFlightSearchAirports(flightScope, form.origin, form.destination, country);
    setForm((prev) => ({ ...prev, origin: coerced.origin, destination: coerced.destination }));
  };

  const updateLeg = (index, field, value) => {
    setLegs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      // Auto chain next origin if destination changes
      if (field === 'destination' && index + 1 < next.length) {
        next[index + 1] = { ...next[index + 1], origin: value };
      }
      return next;
    });
  };

  const addLeg = () => {
    if (legs.length >= 6) return;
    const lastDest = legs[legs.length - 1]?.destination || 'JED';
    setLegs((prev) => [...prev, { origin: lastDest, destination: 'ISB', departureDate: '' }]);
  };

  const removeLeg = (index) => {
    if (legs.length <= 2) return;
    setLegs((prev) => prev.filter((_, i) => i !== index));
  };

  const runSearch = async (overrideForm) => {
    const payload = overrideForm || form;
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const isMulti = tripType === 'multi_city';
      const searchBody = {
        tripType,
        origin: isMulti ? legs[0].origin : payload.origin,
        destination: isMulti ? legs[legs.length - 1].destination : payload.destination,
        departureDate: isMulti ? legs[0].departureDate : payload.departureDate,
        returnDate: tripType === 'round_trip' ? payload.returnDate || null : null,
        legs: isMulti ? legs : [],
        adults: Number(payload.adults) || 1,
        children: Number(payload.children) || 0,
        infants: Number(payload.infants) || 0,
        cabinClass: payload.cabinClass || 'economy',
        maxResults: 25,
      };

      const res = await axios.post(`${API_BASE}/flights/search`, searchBody);
      setResult(res.data);

      // Auto-set price slider max based on returned results
      if (res.data?.offers?.length) {
        const prices = res.data.offers
          .map((o) => parseFloat(o.totalAmount))
          .filter((p) => !isNaN(p));
        if (prices.length) {
          const maxFound = Math.max(...prices);
          setMaxPriceFilter(Math.ceil(maxFound * 1.2));
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Flight search failed. Please verify API connection.');
    } finally {
      setLoading(false);
    }
  };

  const search = async (e) => {
    e.preventDefault();
    await runSearch();
  };

  useEffect(() => {
    const s = location.state;
    if (!s) return;
    if (s.flightScope) setFlightScope(s.flightScope);
    if (s.destinationCountry) setDestinationCountry(s.destinationCountry);
    if (!s.origin || autoSearched.current) return;
    autoSearched.current = true;
    const next = {
      origin: s.origin || 'ISB',
      destination: s.destination || 'JED',
      departureDate: s.departureDate || '',
      returnDate: s.returnDate || '',
      adults: s.adults || 1,
      children: s.children || 0,
      infants: s.infants || 0,
      cabinClass: s.cabinClass || 'economy',
    };
    const scope = s.flightScope || flightScope;
    const country = s.destinationCountry || destinationCountry;
    const coerced = coerceFlightSearchAirports(scope, next.origin, next.destination, country);
    const merged = { ...next, origin: coerced.origin, destination: coerced.destination };
    setForm(merged);
    if (s.autoSearch && merged.departureDate) {
      runSearch(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const submitRequest = async () => {
    if (!selected) return;
    setRequestLoading(true);
    setError('');
    try {
      const isMulti = tripType === 'multi_city';
      const routeSummary = isMulti
        ? legs.map((l) => l.origin).join(' ➔ ') + ' ➔ ' + legs[legs.length - 1].destination
        : `${selected.origin || form.origin} ➔ ${selected.destination || form.destination}`;

      const res = await axios.post(`${API_BASE}/flights/quote-requests`, {
        offerId: selected.offerId,
        offerRequestId: selected.offerRequestId,
        tripType,
        origin: selected.origin || form.origin,
        destination: selected.destination || form.destination,
        departureDate: isMulti ? legs[0].departureDate : form.departureDate,
        returnDate: tripType === 'round_trip' ? form.returnDate || null : null,
        multiCityRouteSummary: isMulti ? routeSummary : null,
        shownAmount: selected.totalAmount,
        shownCurrency: selected.totalCurrency,
        airlineName: selected.airlineName,
        flightNumber: selected.flightNumber,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
        infants: Number(form.infants) || 0,
        ...requestForm,
        offerSnapshotJson: JSON.stringify(selected),
      });

      setRequestDone({
        ...res.data,
        offer: selected,
        customerName: requestForm.customerName,
        customerPhone: requestForm.customerPhone,
        routeSummary,
      });
      setSelected(null);
      setRequestForm({ customerName: '', customerPhone: '', customerEmail: '', notes: '' });
      navigate('/v2/flights/receipt', {
        state: {
          requestId: res.data.requestId,
          trackingPin: res.data.trackingPin,
          customerName: requestForm.customerName,
          customerPhone: requestForm.customerPhone,
          customerEmail: requestForm.customerEmail,
          origin: selected.origin || form.origin,
          destination: selected.destination || form.destination,
          departureDate: form.departureDate,
          airlineName: selected.airlineName,
          flightNumber: selected.flightNumber,
          shownAmount: selected.totalAmount,
          shownCurrency: selected.totalCurrency,
        },
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.message ||
        'Could not submit request';
      setError(msg);
    } finally {
      setRequestLoading(false);
    }
  };

  const getWhatsAppLink = (offer, quoteResult = null) => {
    const isMulti = tripType === 'multi_city';
    const route = isMulti
      ? legs.map((l) => l.origin).join(' ➔ ') + ' ➔ ' + legs[legs.length - 1].destination
      : `${offer.origin || form.origin} to ${offer.destination || form.destination}`;

    const ref = quoteResult?.requestId ? `\nQuote Ref: #${quoteResult.requestId}` : '';
    const name = quoteResult?.customerName || requestForm.customerName ? `\nCustomer: ${quoteResult?.customerName || requestForm.customerName}` : '';
    const phone = quoteResult?.customerPhone || requestForm.customerPhone ? `\nPhone: ${quoteResult?.customerPhone || requestForm.customerPhone}` : '';

    const text = encodeURIComponent(
      `Hello A.R Travels! I am inquiring about flight quotation:${ref}${name}${phone}\n` +
      `Route: ${route}\n` +
      `Airline: ${offer.airlineName} (${offer.flightNumber || 'Scheduled'})\n` +
      `Estimated Fare: ${offer.totalCurrency || 'USD'} ${offer.totalAmount}\n` +
      `Passengers: ${form.adults} Adults${form.children > 0 ? `, ${form.children} Children` : ''}\n` +
      `Please provide the final confirmed fare and availability.`
    );

    return `https://wa.me/${WHATSAPP_PHONE}?text=${text}`;
  };

  const applyOfferToCustomize = (offer) => {
    try {
      const raw = sessionStorage.getItem('customizeBookingDraft');
      const draft = raw ? JSON.parse(raw) : {};
      const next = {
        ...draft,
        includeFlight: true,
        flightOrigin: offer.origin || form.origin,
        flightDestination: offer.destination || form.destination,
        selectedFlightOfferId: offer.offerId,
        selectedFlightAmount: offer.totalAmount,
        selectedFlightCurrency: offer.totalCurrency,
        selectedFlightLabel: `${offer.airlineName || 'Flight'} ${offer.flightNumber || ''}`.trim(),
      };
      sessionStorage.setItem('customizeBookingDraft', JSON.stringify(next));
      sessionStorage.setItem('customizeBookingStep', '1');
    } catch (_) {
      /* ignore */
    }
    navigate('/v2/customized-booking');
  };

  const formatMoney = (amount, currency) => {
    if (!amount) return '—';
    return `${currency || 'USD'} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // Filter and Sort Offers (PRD Section 6.4, 8 & 9)
  const availableAirlines = useMemo(() => {
    if (!result?.offers) return [];
    const set = new Set();
    result.offers.forEach((o) => {
      if (o.airlineName) set.add(o.airlineName);
    });
    return Array.from(set);
  }, [result]);

  const filteredOffers = useMemo(() => {
    if (!result?.offers) return [];

    let list = [...result.offers];

    // 1. Recommendation Category Filter
    if (activeCategoryTab !== 'all') {
      list = list.filter((o) => o.categoryBadges && o.categoryBadges.includes(activeCategoryTab));
    }

    // 2. Filter by Stops
    if (filterStops === '0') {
      list = list.filter((o) => o.stops === 0);
    } else if (filterStops === '1') {
      list = list.filter((o) => o.stops <= 1);
    }

    // 3. Filter by Airline
    if (filterAirline !== 'all') {
      list = list.filter((o) => o.airlineName === filterAirline);
    }

    // 4. Filter by Max Price
    list = list.filter((o) => {
      const price = parseFloat(o.totalAmount);
      return isNaN(price) || price <= maxPriceFilter;
    });

    // 5. Sort By
    list.sort((a, b) => {
      if (sortBy === 'price_asc') {
        return (parseFloat(a.totalAmount) || 0) - (parseFloat(b.totalAmount) || 0);
      }
      if (sortBy === 'duration_asc') {
        return (a.totalDurationMinutes || 9999) - (b.totalDurationMinutes || 9999);
      }
      if (sortBy === 'dep_asc') {
        const da = a.departsAt ? new Date(a.departsAt).getTime() : 0;
        const db = b.departsAt ? new Date(b.departsAt).getTime() : 0;
        return da - db;
      }
      // 'recommended': BestOverall first, then score
      const aIsBest = a.categoryBadges?.includes('BestOverall') ? 1 : 0;
      const bIsBest = b.categoryBadges?.includes('BestOverall') ? 1 : 0;
      if (bIsBest !== aIsBest) return bIsBest - aIsBest;
      return (b.rankingScore || 0) - (a.rankingScore || 0);
    });

    return list;
  }, [result, activeCategoryTab, filterStops, filterAirline, maxPriceFilter, sortBy]);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 12 } }}>
        {/* Header Title with A.R Travels branding */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Chip
              label="A.R Travels"
              color="secondary"
              size="small"
              sx={{ fontWeight: 700, letterSpacing: 0.5 }}
            />
            <Chip
              label="Intelligent Flight Engine"
              variant="outlined"
              size="small"
              sx={{ borderColor: 'secondary.main', color: 'secondary.main', fontWeight: 600 }}
            />
          </Stack>
          <Typography variant="h3" sx={{ fontFamily: 'Fraunces, serif', fontWeight: 700, mb: 1 }}>
            Intelligent Flight Search & Quotations
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
            Compare flights ranked by price, travel time, and connection practicality. Select an itinerary to request a live quotation or chat directly with our travel specialists.
            {returnToCustomize && ' Select a fare and click “Use in customize trip” to return with pricing.'}
          </Typography>
        </Box>

        {/* Main Search Box */}
        <Paper
          component="form"
          onSubmit={search}
          elevation={0}
          sx={{ p: { xs: 2.5, md: 3.5 }, mb: 4, border: `1px solid ${colors.border.light}`, borderRadius: 3 }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Trip type"
              value={flightScope}
              onChange={(e) => handleFlightScopeChange(null, e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {getFlightScopeOptions().map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            {getCountriesForProductLine(flightScope).length > 1 && (
              <TextField
                select
                size="small"
                label="Destination country"
                value={destinationCountry}
                onChange={handleDestinationCountryChange}
                sx={{ minWidth: 220 }}
              >
                {getCountriesForProductLine(flightScope).map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {/* Trip Type Selector (PRD Section 6.2 & 6.2.1) */}
          <Tabs
            value={tripType}
            onChange={handleTripTypeChange}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="one_way" label="One Way" sx={{ fontWeight: 600 }} />
            <Tab value="round_trip" label="Round Trip" sx={{ fontWeight: 600 }} />
            <Tab
              value="multi_city"
              label="Multi-City / Multi-Country"
              sx={{ fontWeight: 700 }}
              icon={<Explore fontSize="small" />}
              iconPosition="end"
            />
          </Tabs>

          {tripType !== 'multi_city' ? (
            /* Standard One-Way / Round-Trip Form */
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  required
                  label="From (Origin)"
                  value={form.origin}
                  onChange={update('origin')}
                >
                  {originAirports.map((a) => (
                    <MenuItem key={a.code} value={a.code} disabled={a.code === form.destination}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  required
                  label="To (Destination)"
                  value={form.destination}
                  onChange={update('destination')}
                >
                  {destinationAirports.map((a) => (
                    <MenuItem key={`to-${a.code}`} value={a.code} disabled={a.code === form.origin}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Departure Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.departureDate}
                  onChange={update('departureDate')}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  required={tripType === 'round_trip'}
                  disabled={tripType === 'one_way'}
                  type="date"
                  label="Return Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.returnDate}
                  onChange={update('returnDate')}
                />
              </Grid>
              <Grid item xs={4} md={1}>
                <TextField fullWidth type="number" label="Adults" value={form.adults} onChange={update('adults')} inputProps={{ min: 1 }} />
              </Grid>
              <Grid item xs={4} md={1}>
                <TextField fullWidth type="number" label="Child" value={form.children} onChange={update('children')} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField fullWidth select label="Cabin Class" value={form.cabinClass} onChange={update('cabinClass')}>
                  <MenuItem value="economy">Economy</MenuItem>
                  <MenuItem value="premium_economy">Premium Economy</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="first">First Class</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                  disabled={loading}
                  sx={{ height: 56, fontWeight: 700 }}
                >
                  Search Available Flights
                </Button>
              </Grid>
            </Grid>
          ) : (
            /* Multi-City / Multi-Country Route Planner (PRD Section 6.2.1) */
            <Box>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Plan your multi-city or multi-country route leg by leg. Specify each destination and departure date for your stay.
              </Alert>

              <Stack spacing={2} sx={{ mb: 3 }}>
                {legs.map((leg, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{ p: 2, bgcolor: 'action.hover', border: `1px dashed ${colors.border.light}`, borderRadius: 2 }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={1}>
                        <Typography fontWeight={700} color="secondary.main">
                          Leg {index + 1}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3.5}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="From"
                          size="small"
                          value={leg.origin}
                          onChange={(e) => updateLeg(index, 'origin', e.target.value)}
                        >
                          {FLIGHT_AIRPORTS.map((a) => (
                            <MenuItem key={`leg-${index}-from-${a.code}`} value={a.code} disabled={a.code === leg.destination}>
                              {a.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={3.5}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="To"
                          size="small"
                          value={leg.destination}
                          onChange={(e) => updateLeg(index, 'destination', e.target.value)}
                        >
                          {FLIGHT_AIRPORTS.map((a) => (
                            <MenuItem key={`leg-${index}-to-${a.code}`} value={a.code} disabled={a.code === leg.origin}>
                              {a.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={10} sm={3}>
                        <TextField
                          fullWidth
                          required
                          type="date"
                          size="small"
                          label="Travel Date"
                          InputLabelProps={{ shrink: true }}
                          value={leg.departureDate}
                          onChange={(e) => updateLeg(index, 'departureDate', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1}>
                        {legs.length > 2 && (
                          <IconButton color="error" onClick={() => removeLeg(index)} title="Remove leg">
                            <RemoveCircleOutline />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddCircleOutline />}
                  onClick={addLeg}
                  disabled={legs.length >= 6}
                >
                  Add Destination Leg
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Route: {legs.map((l) => l.origin).join(' ➔ ')} ➔ {legs[legs.length - 1].destination}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth type="number" label="Adults" value={form.adults} onChange={update('adults')} inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth type="number" label="Child" value={form.children} onChange={update('children')} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth select label="Cabin Class" value={form.cabinClass} onChange={update('cabinClass')}>
                    <MenuItem value="economy">Economy</MenuItem>
                    <MenuItem value="premium_economy">Premium</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                    <MenuItem value="first">First</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                    disabled={loading}
                    sx={{ height: 56, fontWeight: 700 }}
                  >
                    Search Multi-City Itineraries
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {requestDone && (
          <Alert
            severity="success"
            sx={{ mb: 3, p: 2 }}
            onClose={() => setRequestDone(null)}
            action={
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<WhatsApp />}
                href={getWhatsAppLink(requestDone.offer, requestDone)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Inquire on WhatsApp
              </Button>
            }
          >
            <Typography fontWeight={700}>Quotation Request #{requestDone.requestId} Received!</Typography>
            <Typography variant="body2">{requestDone.message}</Typography>
          </Alert>
        )}

        {/* Results & Intelligent Recommendation Section */}
        {result && (
          <Box>
            {/* Intelligent Recommendation Categories (PRD Sections 8 & 9) */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: `1px solid ${colors.border.light}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                Intelligent Recommendations & Categories
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`All Flights (${result.offers?.length || 0})`}
                  clickable
                  color={activeCategoryTab === 'all' ? 'secondary' : 'default'}
                  variant={activeCategoryTab === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategoryTab('all')}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  icon={<Star sx={{ color: '#ffd700 !important' }} />}
                  label="🏆 Best Overall"
                  clickable
                  color={activeCategoryTab === 'BestOverall' ? 'secondary' : 'default'}
                  variant={activeCategoryTab === 'BestOverall' ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategoryTab('BestOverall')}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  icon={<AttachMoney />}
                  label="💵 Cheapest"
                  clickable
                  color={activeCategoryTab === 'Cheapest' ? 'success' : 'default'}
                  variant={activeCategoryTab === 'Cheapest' ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategoryTab('Cheapest')}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  icon={<Speed />}
                  label="⚡ Fastest"
                  clickable
                  color={activeCategoryTab === 'Fastest' ? 'primary' : 'default'}
                  variant={activeCategoryTab === 'Fastest' ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategoryTab('Fastest')}
                  sx={{ fontWeight: 700 }}
                />

                <Button
                  size="small"
                  startIcon={<Tune />}
                  variant={showFilters ? 'contained' : 'outlined'}
                  color="inherit"
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ ml: 'auto' }}
                >
                  {showFilters ? 'Hide Filters' : 'Filters & Sort'}
                </Button>
              </Stack>

              {/* Filters Bar (PRD Section 6.4) */}
              {showFilters && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${colors.border.light}` }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Stops</InputLabel>
                        <Select value={filterStops} label="Stops" onChange={(e) => setFilterStops(e.target.value)}>
                          <MenuItem value="all">Any Number of Stops</MenuItem>
                          <MenuItem value="0">Non-Stop Direct Only</MenuItem>
                          <MenuItem value="1">Up to 1 Stop</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Airline</InputLabel>
                        <Select value={filterAirline} label="Airline" onChange={(e) => setFilterAirline(e.target.value)}>
                          <MenuItem value="all">All Airlines</MenuItem>
                          {availableAirlines.map((airline) => (
                            <MenuItem key={airline} value={airline}>{airline}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sort By</InputLabel>
                        <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                          <MenuItem value="recommended">Intelligent Ranking (Recommended)</MenuItem>
                          <MenuItem value="price_asc">Price: Low to High</MenuItem>
                          <MenuItem value="duration_asc">Duration: Shortest First</MenuItem>
                          <MenuItem value="dep_asc">Departure: Earliest First</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Max Fare: ${maxPriceFilter}
                      </Typography>
                      <Slider
                        value={maxPriceFilter}
                        min={200}
                        max={3000}
                        step={50}
                        onChange={(_, val) => setMaxPriceFilter(val)}
                        valueLabelDisplay="auto"
                        color="secondary"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>

            {(result.usedMockData || result.UsedMockData) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Demo flight prices — amounts change by route, return date, and cabin class. Add a live Duffel API token on the server for real airline fares.
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 3 }}>
              {result.disclaimer}
            </Alert>

            {filteredOffers.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  No flight itineraries match your active filters. Try clearing filters or selecting another category.
                </Typography>
                <Button sx={{ mt: 2 }} onClick={() => { setFilterStops('all'); setFilterAirline('all'); setActiveCategoryTab('all'); }}>
                  Reset Filters
                </Button>
              </Paper>
            ) : (
              <Stack spacing={2.5}>
                {filteredOffers.map((offer) => {
                  const isBestOverall = offer.categoryBadges?.includes('BestOverall');
                  const isCheapest = offer.categoryBadges?.includes('Cheapest');
                  const isFastest = offer.categoryBadges?.includes('Fastest');
                  return (
                    <Paper
                      key={offer.offerId}
                      elevation={0}
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        border: isBestOverall
                          ? '2px solid #8e24aa'
                          : `1px solid ${colors.border.light}`,
                        borderRadius: 3,
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                          borderColor: 'secondary.main',
                        },
                      }}
                    >
                      {/* Badge Ribbon */}
                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.8 }}>
                        {isBestOverall && (
                          <Chip
                            label="🏆 Best Overall Value"
                            sx={{ bgcolor: '#8e24aa', color: '#fff', fontWeight: 700 }}
                            size="small"
                          />
                        )}
                        {isCheapest && (
                          <Chip
                            label="💵 Lowest Fare"
                            color="success"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                        {isFastest && (
                          <Chip
                            label="⚡ Fastest Route"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                        {offer.stops === 0 && (
                          <Chip label="Direct Non-Stop" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
                        )}
                      </Stack>

                      <Grid container spacing={2} alignItems="center">
                        {/* Flight & Airline Details */}
                        <Grid item xs={12} md={5}>
                          <Typography variant="h6" fontWeight={700}>
                            <FlightTakeoff sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                            {offer.airlineName || 'Airline'}
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              {offer.flightNumber}
                            </Typography>
                          </Typography>

                          <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                            {getAirportLabel(offer.origin)} ➔ {getAirportLabel(offer.destination)}
                          </Typography>

                          <Stack direction="row" spacing={2} sx={{ mt: 1, color: 'text.secondary', flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AccessTime sx={{ fontSize: 16, mr: 0.5 }} />
                              <Typography variant="body2">
                                Departs {formatTime(offer.departsAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {offer.duration ? `Duration: ${offer.duration.replace('PT', '').replace('H', 'h ').replace('M', 'm')}` : ''}
                              </Typography>
                            </Box>
                          </Stack>

                          {/* Connection / Layover details (PRD Section 10 & 11) */}
                          {offer.layoverDetails && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.8, color: 'info.main', fontWeight: 600 }}>
                              Layover: {offer.layoverDetails}
                            </Typography>
                          )}

                          {offer.connectionWarning && (
                            <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ py: 0.2, px: 1, mt: 1, fontSize: '0.8rem' }}>
                              {offer.connectionWarning}
                            </Alert>
                          )}
                        </Grid>

                        {/* Practicality & Baggage Features */}
                        <Grid item xs={12} md={3}>
                          <Stack spacing={0.8}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Luggage sx={{ fontSize: 16, mr: 0.8, color: 'text.secondary' }} />
                              <Typography variant="caption" fontWeight={600}>
                                {offer.baggageAllowance || 'Checked bag included'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Class: {offer.cabinClass ? offer.cabinClass.toUpperCase() : 'ECONOMY'}
                            </Typography>
                            {offer.conditionsSummary && (
                              <Typography variant="caption" color="text.secondary">
                                {offer.conditionsSummary}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>

                        {/* Pricing and Action Buttons */}
                        <Grid item xs={12} md={4}>
                          <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={1}>
                            <Typography variant="h4" fontWeight={800} color="secondary.main">
                              {formatMoney(offer.totalAmount, offer.totalCurrency)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Indicative fare · final confirmed on IATA portal
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mt: 1, width: '100%', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                              <Tooltip title="Instant WhatsApp message to A.R Travels with this flight itinerary">
                                <Button
                                  variant="outlined"
                                  color="success"
                                  startIcon={<WhatsApp />}
                                  href={getWhatsAppLink(offer)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="medium"
                                >
                                  WhatsApp
                                </Button>
                              </Tooltip>

                              <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<RequestQuote />}
                                onClick={() => {
                                  setSelected(offer);
                                  setRequestDone(null);
                                }}
                                size="medium"
                                sx={{ fontWeight: 700 }}
                              >
                                Request Quotation
                              </Button>
                            </Stack>

                            {returnToCustomize && (
                              <Button
                                variant="text"
                                color="secondary"
                                size="small"
                                onClick={() => applyOfferToCustomize(offer)}
                              >
                                Use in customize trip
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </Container>

      {/* Flight Quotation Request Modal (PRD Section 13) */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          Request Flight Quotation
          <Typography variant="body2" color="text.secondary">
            Our agents will confirm live IATA availability and provide final booking options.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selected && (
            <>
              <Paper elevation={0} sx={{ p: 2, my: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {selected.airlineName} · {selected.flightNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.origin} ➔ {selected.destination} · {formatMoney(selected.totalAmount, selected.totalCurrency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Passengers: {form.adults} Adult(s){form.children > 0 ? `, ${form.children} Child(ren)` : ''}
                </Typography>
              </Paper>

              <FlightItineraryPanel offer={selected} defaultExpanded compact />

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <TextField
                  required
                  label="Full Name"
                  placeholder="e.g. Muhammad Ali"
                  value={requestForm.customerName}
                  onChange={(e) => setRequestForm((p) => ({ ...p, customerName: e.target.value }))}
                />
                <TextField
                  required
                  label="Phone / WhatsApp Number"
                  placeholder="e.g. +92 300 1234567"
                  value={requestForm.customerPhone}
                  onChange={(e) => setRequestForm((p) => ({ ...p, customerPhone: e.target.value }))}
                />
                <TextField
                  label="Email Address"
                  placeholder="e.g. customer@example.com"
                  value={requestForm.customerEmail}
                  onChange={(e) => setRequestForm((p) => ({ ...p, customerEmail: e.target.value }))}
                />
                <TextField
                  label="Special Requests / Luggage Notes"
                  multiline
                  minRows={2}
                  placeholder="e.g. preferred seating, wheelchair assistance, luggage on connections"
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={requestLoading || !requestForm.customerName || !requestForm.customerPhone}
            onClick={submitRequest}
            sx={{ fontWeight: 700 }}
          >
            {requestLoading ? <CircularProgress size={22} color="inherit" /> : 'Submit Quotation Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <FooterV2 />
    </Box>
  );
};

export default FlightsPageV2;
